import { useMemo } from 'react';
import { useEffectiveOperators } from './useEffectiveOperators';
import { useDesignatedOperatorRelationsAll } from './useDesignatedOperatorRelationsQueries';
import { useUserPreferences } from './useUserPreferences';

/**
 * Combined hook that provides **effective** (preference-scoped) operator lists
 * together with year → PPO cascading logic.
 *
 * **Cascading rules (CR-04):**
 * 1. **Years** are derived from user preferences (when any exist for this module),
 *    otherwise from Settings.  Years are **never** filtered by the selected DO.
 * 2. **DOs** depend on the selected year + module.  When a specific year is
 *    selected the backend's year-scoped `effective-operators` endpoint is used.
 *    When "ALL" years → all effective operators for the module.
 * 3. Year → DO is one-directional: changing a DO never narrows the year list.
 *
 * @param module   Module name (`eno1`, `e53`, `e55`, `e09`, `tdacc`).
 * @param options  Optional cascading filter values.
 */
export function useModuleOperators(
  module: string,
  options: {
    /** Currently selected year (single-year pages). */
    selectedYear?: string;
    /** Currently selected PPO (kept for backward compat — no longer narrows years). */
    selectedPpo?: string;
    /** From year (year-range pages like CN56, ItemsKg). */
    fromYear?: string;
    /** To year (year-range pages like CN56, ItemsKg). */
    toYear?: string;
  } = {},
) {
  const { selectedYear, fromYear, toYear } = options;

  // ─── User preferences: detect whether customisations exist ──────────────────
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  /** True when the user has ≥1 enabled preference for THIS specific module. */
  const hasModulePreferences = useMemo(
    () =>
      preferences.some((p) =>
        p.modules.some((m) => m.name === module && m.enabled),
      ),
    [preferences, module],
  );

  /** True when the user has ANY preference with at least one enabled module. */
  const hasAnyPreferences = useMemo(
    () => preferences.some((p) => p.modules.some((m) => m.enabled)),
    [preferences],
  );

  // ─── Primary PPO source: effective operators (Settings ∩ Preferences) ───────
  const {
    operators: effectiveOperators,
    isLoading: isEffectiveLoading,
    error: effectiveError,
  } = useEffectiveOperators(undefined, module);

  // ─── Year-scoped effective operators (for DO cascading when a year is selected)
  const yearNum = selectedYear ? parseInt(selectedYear) : undefined;
  const {
    operators: yearScopedOperators,
    isLoading: isYearScopedLoading,
  } = useEffectiveOperators(yearNum, module, !!yearNum);

  // ─── Relations data (Settings) — still used for year-range cascading ────────
  const {
    data: relationsData,
    isLoading: isRelationsLoading,
    error: relationsError,
  } = useDesignatedOperatorRelationsAll(module);

  // ─── Resolved operators ────────────────────────────────────────────────────
  // Priority:
  //  1. User has ≥1 enabled preference for THIS module
  //     → use effectiveOperators (Settings ∩ Preferences).
  //  2. User has preferences for OTHER modules but NONE for this module
  //     → empty list (the user deliberately has no customisation for this module).
  //  3. User has NO preferences at all (pristine)
  //     → fall back to Settings operators so behaviour matches pre-CR-04.
  const settingsOperators = useMemo(() => {
    if (!relationsData?.byTargetDO) return [] as string[];
    const ops: string[] = [];
    Object.entries(relationsData.byTargetDO).forEach(([targetDO, relations]) => {
      if (relations.some((r) => r.modules.some((m) => m.name === module && m.enabled))) {
        ops.push(targetDO);
      }
    });
    return ops.sort();
  }, [relationsData, module]);

  const resolvedOperators = useMemo(() => {
    // Case 1: user has active preferences for this module → trust backend
    if (hasModulePreferences) return effectiveOperators;
    // Case 2: user has preferences but not for this module → empty
    if (hasAnyPreferences) return [] as string[];
    // Case 3: no preferences at all (pristine) → fall back to Settings
    return effectiveOperators.length > 0 ? effectiveOperators : settingsOperators;
  }, [hasModulePreferences, hasAnyPreferences, effectiveOperators, settingsOperators]);

  // ─── Derived: available years ──────────────────────────────────────────────
  // If the user has ≥1 preference for this module → show only years from those
  // preferences.  If the user has preferences but none for this module → empty.
  // Otherwise fall back to all Settings years (pre-CR-04 behaviour).
  const availableYears = useMemo(() => {
    if (hasModulePreferences) {
      const years = new Set<number>();
      preferences.forEach((p) => {
        if (p.modules.some((m) => m.name === module && m.enabled)) {
          years.add(p.accountingYear);
        }
      });
      return Array.from(years).sort((a, b) => b - a);
    }

    // User has preferences for other modules but not this one → empty
    if (hasAnyPreferences) return [] as number[];

    // No preferences → derive from Settings relations
    if (!relationsData?.byYear) return [] as number[];
    const years: number[] = [];
    Object.entries(relationsData.byYear).forEach(([year, relations]) => {
      const hasEffectiveDO = relations.some(
        (r) =>
          resolvedOperators.includes(r.targetDO) &&
          r.modules.some((m) => m.name === module && m.enabled),
      );
      if (hasEffectiveDO) years.push(parseInt(year));
    });
    return years.sort((a, b) => b - a);
  }, [hasModulePreferences, hasAnyPreferences, preferences, relationsData, resolvedOperators, module]);

  // ─── Cascading: PPOs for selected single year ──────────────────────────────
  // When a year is selected and user has preferences, use the backend's
  // year-scoped effective operators. Otherwise fall back to Settings-derived.
  const operatorsForYear = useMemo(() => {
    if (!selectedYear) return resolvedOperators;
    if (hasModulePreferences) return yearScopedOperators;
    // No enabled preferences → filter Settings operators by year
    if (!relationsData?.byYear?.[selectedYear]) return resolvedOperators;
    return relationsData.byYear[selectedYear]
      .filter(
        (r) =>
          resolvedOperators.includes(r.targetDO) &&
          r.modules.some((m) => m.name === module && m.enabled),
      )
      .map((r) => r.targetDO)
      .sort();
  }, [selectedYear, resolvedOperators, hasModulePreferences, yearScopedOperators, relationsData, module]);

  // ─── Cascading: PPOs for year range (CN56 / ItemsKg style) ────────────────
  const operatorsForYearRange = useMemo(() => {
    if (!relationsData?.byTargetDO) return resolvedOperators;
    if (!fromYear && !toYear) return resolvedOperators;

    const filtered: string[] = [];
    resolvedOperators.forEach((targetDO) => {
      const relations = relationsData.byTargetDO[targetDO];
      if (!relations) return;
      const moduleYears = relations
        .filter((r) => r.modules.some((m) => m.name === module && m.enabled))
        .map((r) => r.year)
        .sort((a, b) => a - b);
      if (moduleYears.length === 0) return;
      const minY = moduleYears[0];
      const maxY = moduleYears[moduleYears.length - 1];
      let include = true;
      if (fromYear && toYear) {
        include = maxY >= parseInt(fromYear) && minY <= parseInt(toYear);
      } else if (fromYear) {
        include = maxY >= parseInt(fromYear);
      } else if (toYear) {
        include = minY <= parseInt(toYear);
      }
      if (include) filtered.push(targetDO);
    });
    return filtered.sort();
  }, [fromYear, toYear, relationsData, resolvedOperators, module]);

  // ─── Years: never filtered by selected DO ──────────────────────────────────
  // (Requirement 3: years must not depend on the selected operator.)
  const yearsForOperator = availableYears;

  return {
    /** Effective operators — preference-scoped when customised, Settings when not. */
    operators: resolvedOperators,
    /** Years that contain ≥1 effective PPO (descending). */
    availableYears,
    /** PPOs filtered to the selected year (cascading). Falls back to all operators. */
    operatorsForYear,
    /** PPOs filtered to the from/to year range (cascading). Falls back to all operators. */
    operatorsForYearRange,
    /** Years for the selected PPO — now always equals availableYears (no DO cascading). */
    yearsForOperator,
    /** Whether the user has ≥1 enabled preference for this module. */
    hasPreferences: hasModulePreferences,
    /** Raw relations data (for pages that need advanced cascading or validation). */
    relationsData,
    /** True while any data source is still loading. */
    isLoading: isEffectiveLoading || isRelationsLoading || isPreferencesLoading,
    /** True while PPO data is loading. */
    isPpoLoading: isEffectiveLoading || isYearScopedLoading,
    /** Effective-operators error. */
    ppoError: effectiveError,
    /** Relations error. */
    relationsError,
  };
}







